import React, { useCallback, useEffect, useState } from 'react';
import { ToastNotification } from 'engage/components/Toast/Toast';
import { useUserContext } from 'engage/context/UserContext';
import useIsAssignmentsRead from 'engage/hooks/useIsAssignmentsRead';
import VideoService from 'engage/service/VideoService';
import { useTranslation } from 'react-i18next';

/**
 * Custom hook to manage story permissions and assignments.
 */

export const useStoryPermissions = ({ videoId, videoAuthor }) => {
    const [assignments, setAssignments] = useState([]);
    const [permissions, setPermissions] = useState([]);

    const { user } = useUserContext();
    const { t } = useTranslation();

    useIsAssignmentsRead({ storyId: videoId, assignments, user, updateAssignments: setAssignments });

    const inviteForScenes = useCallback(async (sceneIds, name, email, message, seeStoryLine, canEditClips) => {
        await VideoService.inviteUser(videoId, sceneIds, email, message, seeStoryLine, canEditClips);
        const assignments = await VideoService.getVideoAssignmentsList(videoId);
        setAssignments(assignments);
        const permissions = await VideoService.getStoryPermissions(videoId);
        setPermissions(permissions);
        ToastNotification({ message: `${t('storyboard.editor.user_invited')}`, iconName: 'success' });
    }, [videoId, t]);

    const manageTeam = useCallback(
        async newSettings => {
            const permissionsUsers = [];
            const assignmentsUsers = [];

            for (let username in newSettings) {
                const { permissions: newPermissions = null, scenes: newScenes = null } = newSettings[username] || {};
                if (username !== videoAuthor?.username && newPermissions) {
                    permissionsUsers.push({ username, actions: newPermissions });
                    // Remove user without assigned clips from the assignments list if he has no permissions and no scenes
                    if (!newPermissions?.read && !newPermissions?.update && (newScenes !== null && newScenes.length == 0)) {
                        assignmentsUsers.push({ username, sceneIds: [] });
                    }
                }
            }

            permissionsUsers.length > 0 && (await VideoService.updateUserPermissions(videoId, permissionsUsers));

            for (let username in newSettings) {
                const { scenes: newScenes = null } = newSettings[username] || {};
                if (username !== videoAuthor?.username && newScenes !== null) {
                    assignmentsUsers.push({ username, sceneIds: newScenes });
                }
            }

            assignmentsUsers.length > 0 && (await VideoService.bulkUpdateAssignments(videoId, assignmentsUsers));

            const permissions = await VideoService.getStoryPermissions(videoId);
            setPermissions(permissions);
            const assignmentsResponse = await VideoService.getVideoAssignmentsList(videoId);
            setAssignments(assignmentsResponse);
        },
        [user, assignments, videoAuthor, permissions]
    );

    const remindUser = useCallback(async username => {
        if (username) {
            try {
                await VideoService.sendReminder(videoId, username);
                ToastNotification({ message: `${t('storyboard.editor.reminder_sent')}`, iconName: 'success' });
            } catch (e) {
                if (e.message.includes('429')) {
                    ToastNotification({ message: `${t('storyboard.editor.reminder_already_sent')}` });
                }
            }
        }
    }, [videoId]);


    const reviseRequest = useCallback(async (username, name, id) => {
        try {
            await VideoService.updateUserAssignmentsCompletion(videoId, username, { completed: false });
            const assignments = await VideoService.getVideoAssignmentsList(videoId);
            setAssignments(assignments);
            ToastNotification({
                message: t('clip_organizer.toast.assignments_successfully_revised', { userName: name }),
                iconName: 'success',
            });
        } catch (err) {
            ToastNotification({
                message: t('clip_organizer.toast.error_while_saving'),
            });
        }
    }, [videoId]);

    const changeUserPermissions = useCallback(async (username, id, settings) => {
        await VideoService.updateUserPermissions(videoId, [{ username, actions: settings }]);
        const permissions = await VideoService.getStoryPermissions(videoId);
        setPermissions(permissions);
    }, [videoId]);

    const completeUserAssignments = useCallback(async () => {
        try {
            await VideoService.completeAssignment(videoId, { completed: true });
            const assignments = await VideoService.getVideoAssignmentsList(videoId);
            setAssignments(assignments);
            ToastNotification({
                message: t('clip_organizer.toast.project_successfully_completed'),
                iconName: 'success',
            });
        } catch (err) {
            ToastNotification({
                message: t('clip_organizer.toast.error_while_saving'),
            });
        }
    }, [videoId, t]);

    useEffect(() => {
        const initAssignments = async () => {
            // Get assignments list
            const assignments = await VideoService.getVideoAssignmentsList(videoId);
            setAssignments(assignments);
            // Get story permissions
            const permissions = await VideoService.getStoryPermissions(videoId);
            setPermissions(permissions);
        }

        initAssignments();
    }, [])

    return {
        assignments,
        permissions,
        manageTeam,
        inviteForScenes,
        remindUser,
        reviseRequest,
        changeUserPermissions,
        completeUserAssignments,
    }
}